(() => {
  if (window.__cartLineAssociatedRecsInit) return;
  window.__cartLineAssociatedRecsInit = true;

  const escapeHTML = (str) =>
    String(str ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoneyGBP = (cents) =>
    (Number(cents || 0) / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });

  const getItemCountFromSections = (sections) => {
    if (!sections) return null;
    try {
      const firstKey = Object.keys(sections)[0];
      const html = firstKey ? sections[firstKey] : '';
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      const count = countText ? parseInt(countText, 10) : NaN;
      return Number.isFinite(count) ? count : null;
    } catch {
      return null;
    }
  };

  const dispatchThemeCartUpdate = (variantId, qty, sections, cartResource) => {
    const itemCount = getItemCountFromSections(sections) ?? cartResource?.item_count ?? null;

    const ev = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartResource || {},
        sourceId: String(variantId || ''),
        data: {
          source: 'cart-line-associated-recs',
          itemCount: itemCount ?? (Number(qty) || 1),
          variantId: String(variantId || ''),
          sections: sections || null,
        },
      },
    });

    document.dispatchEvent(ev);
  };

  // ----- Flyout (single instance globally) -----
  const ensureFlyout = () => {
    let overlay = document.querySelector('.cart-line-recs-flyout__overlay');
    let flyout = document.querySelector('.cart-line-recs-flyout');

    if (overlay && flyout) return { overlay, flyout };

    overlay = document.createElement('div');
    overlay.className = 'cart-line-recs-flyout__overlay';

    flyout = document.createElement('div');
    flyout.className = 'cart-line-recs-flyout';
    flyout.innerHTML = `
      <div class="cart-line-recs-flyout__header">
        <div class="cart-line-recs-flyout__header-left">
          <button type="button" class="cart-line-recs-flyout__back" aria-label="Back">
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="cart-line-recs-flyout__titlebar" data-flyout-title>Complete with</div>
        </div>

        <button type="button" class="cart-line-recs-flyout__close" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="cart-line-recs-flyout__content" data-flyout-content></div>
    `;

    document.body.appendChild(overlay);
    document.body.appendChild(flyout);

    const close = () => {
      flyout.classList.remove('is-active');
      overlay.classList.remove('is-active');
      flyout.classList.remove('has-back');
      document.body.style.overflow = '';
      flyout.__onBack = null;

      const content = flyout.querySelector('[data-flyout-content]');
      if (content) content.innerHTML = '';
    };

    overlay.addEventListener('click', close);
    flyout.querySelector('.cart-line-recs-flyout__close')?.addEventListener('click', close);

    flyout.querySelector('.cart-line-recs-flyout__back')?.addEventListener('click', () => {
      if (typeof flyout.__onBack === 'function') flyout.__onBack();
    });

    return { overlay, flyout };
  };

  const openFlyout = () => {
    const { overlay, flyout } = ensureFlyout();
    flyout.classList.add('is-active');
    overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  };

  const closeFlyout = () => {
    const overlay = document.querySelector('.cart-line-recs-flyout__overlay');
    const flyout = document.querySelector('.cart-line-recs-flyout');
    if (!overlay || !flyout) return;
    flyout.classList.remove('is-active');
    overlay.classList.remove('is-active');
    flyout.classList.remove('has-back');
    document.body.style.overflow = '';
    flyout.__onBack = null;
  };

  const setFlyoutTitle = (title) => {
    const { flyout } = ensureFlyout();
    const t = flyout.querySelector('[data-flyout-title]');
    if (t) t.textContent = String(title || '');
  };

  const setFlyoutBack = (enabled, onBack) => {
    const { flyout } = ensureFlyout();
    flyout.classList.toggle('has-back', !!enabled);
    flyout.__onBack = enabled ? onBack : null;
  };

  const readCategoryData = (container, categoryId) => {
    const el = container.querySelector(
      `[data-cart-line-recs-category][data-category="${CSS.escape(String(categoryId))}"]`
    );
    if (!el) return { label: '', prefix: '', products: [] };

    const label = el.dataset.label || '';
    const prefix = el.dataset.prefix || '';

    let products = [];
    try {
      products = JSON.parse(el.textContent || '[]') || [];
    } catch {
      products = [];
    }

    return { label, prefix, products };
  };

  const renderListView = (container, categoryId) => {
    const { flyout } = ensureFlyout();
    const flyoutContent = flyout.querySelector('[data-flyout-content]');
    if (!flyoutContent) return;

    const heading = (container.dataset.heading || 'Complete with').trim();
    const { label, prefix, products } = readCategoryData(container, categoryId);

    const title = `${heading}${label ? ` — ${prefix ? `${prefix} ` : ''}${label}` : ''}`;
    setFlyoutTitle(title);

    const listHtml = (products || [])
      .map((p) => {
        const img = p.image
          ? `<img src="${escapeHTML(p.image)}" alt="${escapeHTML(p.title || '')}" loading="lazy">`
          : '';

        const metaBits = [];
        if (p.customQuantity) metaBits.push(`Box qty: ${escapeHTML(p.customQuantity)}`);

        return `
          <div class="cart-line-recs-flyout__item" data-handle="${escapeHTML(p.handle || '')}">
            <div class="cart-line-recs-flyout__thumb">${img}</div>

            <div class="cart-line-recs-flyout__item-main">
              <div class="cart-line-recs-flyout__item-title">${escapeHTML(p.title || '')}</div>
              ${metaBits.length ? `<div class="cart-line-recs-flyout__item-meta">${metaBits.join('<br>')}</div>` : ''}
            </div>

            <div class="cart-line-recs-flyout__item-actions" style="display:flex; flex-direction:column; align-items:flex-end; align-self:stretch;">
              <div class="cart-line-recs-flyout__item-price">${escapeHTML(p.price_money || '')}</div>
              <button type="button" class="cart-line-recs-flyout__view" data-view style="margin-top:auto;">View</button>
            </div>

          </div>
        `;
      })
      .join('');

    flyoutContent.innerHTML = `
      <div class="cart-line-recs-flyout__list" data-list>
        ${listHtml || `<div style="opacity:.7;">No items found.</div>`}
      </div>
    `;

    setFlyoutBack(false, null);
    openFlyout();

    flyoutContent.querySelector('[data-list]')?.addEventListener('click', (e) => {
      const viewBtn = e.target.closest('[data-view]');
      const itemEl = e.target.closest('.cart-line-recs-flyout__item');
      if (!itemEl || !viewBtn) return;

      const handle = itemEl.getAttribute('data-handle');
      if (!handle) return;

      openProductDetail(container, handle, categoryId);
    });
  };

  const stripMM = (val) => String(val || '').trim().replace(/\s*mm\s*$/i, '').trim();

  const formatDims = (p) => {
    const h = stripMM(p.__customThickness);
    const w = stripMM(p.__customWidth);
    const l = stripMM(p.__customLength);

    const parts = [];
    if (h) parts.push(`h${escapeHTML(h)}`);
    if (w) parts.push(`w${escapeHTML(w)}`);
    if (l) parts.push(`l${escapeHTML(l)}`);

    return parts.length ? `${parts.join(' x ')}mm` : '';
  };

  const openProductDetail = async (container, handle, categoryId) => {
    const parentKey = (container.dataset.parentKey || '').trim();
    const parentTitle = (container.dataset.parentTitle || '').trim();

    // ✅ NEW: parent handle (needed for PDP adds / stable grouping)
    const parentHandle = (container.dataset.parentHandle || '').trim();

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product fetch failed');
      const product = await res.json();

      const { products } = readCategoryData(container, categoryId);
      const meta = (products || []).find((p) => String(p.handle) === String(handle)) || {};

      product.__customQuantity = (meta.customQuantity || '').trim();
      product.__customThickness = (meta.thickness || '').trim();
      product.__customWidth = (meta.width || '').trim();
      product.__customLength = (meta.length || '').trim();

      product.__recoParentKey = parentKey;
      product.__recoParentTitle = parentTitle;

      // ✅ NEW: store parent handle for add-to-cart properties
      product.__recoParentHandle = parentHandle;

      const { flyout } = ensureFlyout();
      const flyoutContent = flyout.querySelector('[data-flyout-content]');
      const firstVariant = product.variants?.[0];
      if (!firstVariant || !flyoutContent) return;

      const dimsText = formatDims(product);

      const variantsHTML =
        (product.variants?.length || 0) > 1
          ? `
            <div class="cart-line-recs-flyout__field">
              <label class="cart-line-recs-flyout__label">Variant</label>
              <select class="cart-line-recs-flyout__select" data-variant-select>
                ${product.variants
                  .map(
                    (v) => `
                  <option value="${v.id}" ${v.available ? '' : 'disabled'}>
                    ${escapeHTML(v.title)} - ${formatMoneyGBP(v.price)} ${v.available ? '' : '(Sold out)'}
                  </option>
                `
                  )
                  .join('')}
              </select>
            </div>
          `
          : '';

      const variantQtyRowHTML = `
        <div class="cart-line-recs-flyout__variant-qty-row">
          ${variantsHTML || '<div></div>'}
          <div class="cart-line-recs-flyout__field">
            <label class="cart-line-recs-flyout__label">Quantity</label>
            <input class="cart-line-recs-flyout__input" type="number" min="1" value="1" data-qty>
          </div>
        </div>
      `;

      flyoutContent.innerHTML = `
        <img class="cart-line-recs-flyout__image" src="${product.featured_image || ''}" alt="${escapeHTML(
        product.title || ''
      )}" loading="lazy">

        <div class="cart-line-recs-flyout__title">${escapeHTML(product.title || '')}</div>

        ${
          product.__customQuantity
            ? `<div class="cart-line-recs-flyout__boxqty">Box quantity: ${escapeHTML(product.__customQuantity)}</div>`
            : ''
        }

        <div class="cart-line-recs-flyout__price" data-price>${formatMoneyGBP(firstVariant.price)}</div>

        <div class="cart-line-recs-flyout__desc">${product.description || ''}</div>

        ${
          dimsText
            ? `
          <div class="cart-line-recs-flyout__dims">
            <table class="cart-line-recs-flyout__dims-table">
              <tr>
                <td class="cart-line-recs-flyout__dims-label">Dimensions</td>
                <td class="cart-line-recs-flyout__dims-value">${dimsText}</td>
              </tr>
            </table>
          </div>
        `
            : ''
        }

        ${variantQtyRowHTML}

        <button type="button" class="cart-line-recs-flyout__btn" data-add>Add to cart</button>
      `;

      setFlyoutTitle('Product details');
      setFlyoutBack(true, () => renderListView(container, categoryId));
      openFlyout();

      const variantSelect = flyoutContent.querySelector('[data-variant-select]');
      const priceEl = flyoutContent.querySelector('[data-price]');
      const qtyEl = flyoutContent.querySelector('[data-qty]');
      const addBtn = flyoutContent.querySelector('[data-add]');

      variantSelect?.addEventListener('change', (ev) => {
        const v = product.variants.find((x) => String(x.id) === String(ev.target.value));
        if (v && priceEl) priceEl.textContent = formatMoneyGBP(v.price);
      });

      addBtn?.addEventListener('click', async () => {
        const variantId = variantSelect ? variantSelect.value : product.variants[0].id;
        const qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;

        addBtn.disabled = true;

        try {
          const cartItemsComponents = document.querySelectorAll('cart-items-component');
          const sectionsToUpdate = new Set();
          cartItemsComponents.forEach((el) => {
            if (el instanceof HTMLElement && el.dataset.sectionId) sectionsToUpdate.add(el.dataset.sectionId);
          });

          const fd = new FormData();
          fd.append('id', String(variantId));
          fd.append('quantity', String(qty));

          // Existing (cart context)
          if (product.__recoParentKey) fd.append('properties[_reco_parent_key]', String(product.__recoParentKey));
          if (product.__recoParentTitle) fd.append('properties[_reco_parent_title]', String(product.__recoParentTitle));

          // ✅ NEW: stable parent identity (works for PDP adds + cart grouping)
          if (product.__recoParentHandle) fd.append('properties[_reco_parent_handle]', String(product.__recoParentHandle));

          fd.append('properties[_reco_source]', 'cart-line-associated-recs');

          if (sectionsToUpdate.size) fd.append('sections', Array.from(sectionsToUpdate).join(','));
          fd.append('sections_url', window.location.pathname);

          const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

          const res2 = await fetch(addUrl, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
          const json = await res2.json();
          if (!res2.ok || json?.status) throw new Error(json?.message || 'Add to cart failed');

          let cartResource = {};
          try {
            const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            cartResource = await cartRes.json();
          } catch {}

          dispatchThemeCartUpdate(variantId, qty, json.sections, cartResource);
          closeFlyout();
        } catch (err) {
          console.error(err);
        } finally {
          addBtn.disabled = false;
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const initLineRecs = (container) => {
    if (container.__clrBound) return;
    container.__clrBound = true;

    const pills = container.querySelectorAll('.cart-line-recs__pill');

    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const cat = pill.getAttribute('data-category');
        if (!cat) return;

        pills.forEach((p) => p.classList.remove('is-active'));
        pill.classList.add('is-active');

        renderListView(container, cat);
      });
    });
  };

  // Handle clicks on child cart items to open flyout instead of navigating to product page
  const handleChildItemClick = async (e) => {
    const trigger = e.target.closest(
      [
        'a.cart-items__title',
        'a.cart-items__media-container',
        'a.cart-drawer-item__title',
        'a.cart-drawer-item__media',
        'button.cart-drawer-item__child-trigger',
      ].join(', ')
    );
    if (!trigger) return;

    const row = trigger.closest('[data-is-reco-child="1"]');
    if (!row) return;

    e.preventDefault();

    const buttonHandle = trigger.getAttribute('data-child-product-handle');
    let handle = buttonHandle ? buttonHandle.trim() : '';

    if (!handle) {
      const href = trigger.getAttribute('href');
      if (!href) return;
      const match = href.match(/\/products\/([^/?]+)/);
      if (!match || !match[1]) return;
      handle = match[1];
    }

    const parentKey = row.dataset.parentKey || '';
    const parentHandle = row.dataset.parentHandle || '';

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product fetch failed');
      const product = await res.json();

      // Get metafield data from row attributes
      const qty = (row.dataset.productQty || '').trim();
      console.log('DEBUG: row.dataset.productQty =', row.dataset.productQty, 'qty =', qty);
      product.__customQuantity = qty;
      product.__customThickness = (row.dataset.productThickness || '').trim();
      product.__customWidth = (row.dataset.productWidth || '').trim();
      product.__customLength = (row.dataset.productLength || '').trim();

      product.__recoParentKey = parentKey;
      product.__recoParentHandle = parentHandle;

      renderProductDetailInFlyout(product, parentKey);
    } catch (err) {
      console.error('Failed to open product detail:', err);
    }
  };

  const renderProductDetailInFlyout = (product, parentKey) => {
    const { flyout } = ensureFlyout();
    const flyoutContent = flyout.querySelector('[data-flyout-content]');
    const firstVariant = product.variants?.[0];
    if (!firstVariant || !flyoutContent) return;

    const dimsText = formatDims(product);

    const variantsHTML =
      (product.variants?.length || 0) > 1
        ? `
          <div class="cart-line-recs-flyout__field">
            <label class="cart-line-recs-flyout__label">Variant</label>
            <select class="cart-line-recs-flyout__select" data-variant-select>
              ${product.variants
                .map(
                  (v) => `
                <option value="${v.id}" ${v.available ? '' : 'disabled'}>
                  ${escapeHTML(v.title)} - ${formatMoneyGBP(v.price)} ${v.available ? '' : '(Sold out)'}
                </option>
              `
                )
                .join('')}
            </select>
          </div>
        `
        : '';

    const variantQtyRowHTML = `
      <div class="cart-line-recs-flyout__variant-qty-row">
        ${variantsHTML || '<div></div>'}
        <div class="cart-line-recs-flyout__field">
          <label class="cart-line-recs-flyout__label">Quantity</label>
          <input class="cart-line-recs-flyout__input" type="number" min="1" value="1" data-qty>
        </div>
      </div>
    `;

    flyoutContent.innerHTML = `
      <img class="cart-line-recs-flyout__image" src="${product.featured_image || ''}" alt="${escapeHTML(
      product.title || ''
    )}" loading="lazy">

      <div class="cart-line-recs-flyout__title">${escapeHTML(product.title || '')}</div>

      ${
        product.__customQuantity
          ? `<div class="cart-line-recs-flyout__boxqty">Box quantity: ${escapeHTML(product.__customQuantity)}</div>`
          : ''
      }

      <div class="cart-line-recs-flyout__price" data-price>${formatMoneyGBP(firstVariant.price)}</div>

      <div class="cart-line-recs-flyout__desc">${product.description || ''}</div>

      ${
        dimsText
          ? `
        <div class="cart-line-recs-flyout__dims">
          <table class="cart-line-recs-flyout__dims-table">
            <tr>
              <td class="cart-line-recs-flyout__dims-label">Dimensions</td>
              <td class="cart-line-recs-flyout__dims-value">${dimsText}</td>
            </tr>
          </table>
        </div>
      `
          : ''
      }

      ${variantQtyRowHTML}

      <button type="button" class="cart-line-recs-flyout__btn" data-add>Add to cart</button>
    `;

    setFlyoutTitle('Product details');
    setFlyoutBack(true, () => closeFlyout());
    openFlyout();

    const variantSelect = flyoutContent.querySelector('[data-variant-select]');
    const priceEl = flyoutContent.querySelector('[data-price]');
    const qtyEl = flyoutContent.querySelector('[data-qty]');
    const addBtn = flyoutContent.querySelector('[data-add]');

    variantSelect?.addEventListener('change', (ev) => {
      const v = product.variants.find((x) => String(x.id) === String(ev.target.value));
      if (v && priceEl) priceEl.textContent = formatMoneyGBP(v.price);
    });

    addBtn?.addEventListener('click', async () => {
      const variantId = variantSelect ? variantSelect.value : product.variants[0].id;
      const qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;

      addBtn.disabled = true;

      try {
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionsToUpdate = new Set();
        cartItemsComponents.forEach((el) => {
          if (el instanceof HTMLElement && el.dataset.sectionId) sectionsToUpdate.add(el.dataset.sectionId);
        });

        const fd = new FormData();
        fd.append('id', String(variantId));
        fd.append('quantity', String(qty));

        if (parentKey) fd.append('properties[_reco_parent_key]', String(parentKey));
        if (product.__recoParentHandle) fd.append('properties[_reco_parent_handle]', String(product.__recoParentHandle));
        fd.append('properties[_reco_source]', 'cart-child-item-flyout');

        if (sectionsToUpdate.size) fd.append('sections', Array.from(sectionsToUpdate).join(','));
        fd.append('sections_url', window.location.pathname);

        const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

        const res2 = await fetch(addUrl, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
        const json = await res2.json();
        if (!res2.ok || json?.status) throw new Error(json?.message || 'Add to cart failed');

        let cartResource = {};
        try {
          const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          cartResource = await cartRes.json();
        } catch {}

        dispatchThemeCartUpdate(variantId, qty, json.sections, cartResource);
        closeFlyout();
      } catch (err) {
        console.error(err);
      } finally {
        addBtn.disabled = false;
      }
    });
  };

  const boot = (root = document) => {
    root.querySelectorAll('[data-cart-line-recs]').forEach(initLineRecs);
  };

  const bindGlobalHandlers = () => {
    if (window.__cartLineRecsChildClickBound) return;
    window.__cartLineRecsChildClickBound = true;
    document.addEventListener('click', handleChildItemClick);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      bindGlobalHandlers();
    });
  } else {
    boot();
    bindGlobalHandlers();
  }

  // re-init after cart section morphs/updates
  document.addEventListener('cart:update', () => {
    requestAnimationFrame(() => requestAnimationFrame(() => boot()));
  });
})();
